import { ChatMentionDetails } from "./ChatMentionDetails";
import { useChatMentions } from "./ChatMentionsContext";
import { t } from "../../i18n";
import { beginPointerResize } from "../shared/pointer-resize";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  FileCode2,
  GitBranch,
  BarChart3,
  GripVertical,
  Maximize2,
  History,
  Monitor,
  ShieldCheck,
  Server,
  TerminalSquare,
  X,
  Package,
} from "lucide-react";
import { FileTab } from "../game-assets/FileTab";
import { GitLogTab } from "../game-assets/GitLogTab";
import { GitTab } from "../game-assets/GitTab";
import { PreviewTab } from "../game-assets/PreviewTab";
import { TerminalTab } from "../game-assets/TerminalTab";
import type { Environment } from "./ChatDevelopmentPanel.types";
import { api } from "../../api";
import { ChatUsagePanel } from "./ChatUsagePanel";
import { ConversationCommandRulesPanel } from "./ConversationCommandRulesPanel";
import { ChatArtifactsTab } from "./ChatArtifactsTab";
import { useConversationArtifacts } from "../artifacts/useConversationArtifacts";
import {
  isContainerWorkspaceType,
  normalizeAgentWorkspaceMode,
} from "../agent-workspaces/workspace-type";

export function ChatDevelopmentPanel({
  conversationId,
  workspaceId: initialWorkspaceId,
  repositoryId: initialRepositoryId,
  developmentConfigured,
  approvalsAvailable,
  usageRevision,
  conversationRevision,
  onClose,
}: {
  conversationId: string;
  workspaceId?: string | null;
  repositoryId?: string | null;
  developmentConfigured: boolean;
  approvalsAvailable: boolean;
  usageRevision: number;
  conversationRevision: string;
  onClose: () => void;
}) {
  const mentions = useChatMentions();
  const selectedMention = mentions?.selected;
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState("usage");
  const [mountedTabs, setMountedTabs] = useState(() => new Set(["usage"]));
  useEffect(() => {
    if (selectedMention) setTab("mention");
  }, [selectedMention]);
  const [openFiles, setOpenFiles] = useState<string[]>([]);
  const [environment, setEnvironment] = useState<Environment>();
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(
    initialWorkspaceId ?? null,
  );
  const [selectedRepositoryId, setSelectedRepositoryId] = useState<
    string | null
  >(initialRepositoryId ?? null);
  const [width, setWidth] = useState(() =>
    Math.min(
      560,
      Math.max(
        360,
        Number(localStorage.getItem("lumen:development-panel-width")) || 520,
      ),
    ),
  );
  const panel = useRef<HTMLElement>(null);
  const {
    artifacts: conversationArtifacts,
    loading: artifactsLoading,
    error: artifactsError,
    reload: reloadArtifacts,
  } = useConversationArtifacts(
    conversationId,
    conversationRevision,
    developmentConfigured || approvalsAvailable,
  );
  useEffect(() => {
    void api<Environment>(
      `/conversations/${conversationId}/development-workspace`,
    ).then((next) => {
      setEnvironment(next);
      if (!initialWorkspaceId && next.configured)
        setSelectedWorkspaceId(next.configured.workspaceId);
      if (initialRepositoryId == null) {
        const workspaceForRepository =
          initialWorkspaceId ?? next.configured?.workspaceId;
        const configuredRepository =
          next.configurations?.find(
            (configuration) =>
              configuration.workspaceId === workspaceForRepository,
          )?.repositoryId ??
          (next.configured?.workspaceId === workspaceForRepository
            ? (next.configured?.repositoryId ?? null)
            : null);
        if (configuredRepository) setSelectedRepositoryId(configuredRepository);
      }
    });
  }, [conversationId, initialRepositoryId, initialWorkspaceId]);
  useEffect(() => {
    setSelectedWorkspaceId(initialWorkspaceId ?? null);
    setSelectedRepositoryId(initialRepositoryId ?? null);
  }, [initialRepositoryId, initialWorkspaceId]);
  useEffect(() => {
    if (!developmentConfigured && tab === "terminal")
      setTab(approvalsAvailable ? "approvals" : "usage");
  }, [approvalsAvailable, developmentConfigured, tab]);
  useEffect(() => {
    if (
      conversationArtifacts &&
      !conversationArtifacts.length &&
      tab === "artifacts"
    )
      setTab("usage");
  }, [conversationArtifacts, tab]);
  useEffect(() => {
    localStorage.setItem("lumen:development-panel-width", String(width));
  }, [width]);
  const resize = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const available =
      panel.current?.parentElement?.getBoundingClientRect().width ??
      window.innerWidth;
    beginPointerResize(event, {
      axis: "x",
      cursor: "col-resize",
      initialValue: panel.current?.getBoundingClientRect().width ?? width,
      minimum: 360,
      maximum: Math.max(360, available - 320),
      onValueChange: setWidth,
    });
  };
  const openFile = (path: string) => {
    setOpenFiles((current) =>
      current.includes(path) ? current : [...current, path],
    );
    setTab(path);
  };
  const closeFile = (path: string) => {
    setOpenFiles((current) => current.filter((item) => item !== path));
    if (tab === path) setTab("usage");
  };
  const selection = useMemo(
    () => ({
      workspaceId: selectedWorkspaceId,
      repositoryId: selectedRepositoryId,
    }),
    [selectedRepositoryId, selectedWorkspaceId],
  );
  const selectedConfiguration = environment?.configurations?.find(
    (configuration) =>
      configuration.workspaceId === selectedWorkspaceId &&
      configuration.repositoryId === selectedRepositoryId,
  );
  const selectedWorkspaceName =
    selectedConfiguration?.workspaceName ??
    environment?.templates?.find(
      (template) => template.id === selectedWorkspaceId,
    )?.name ??
    environment?.configured?.workspaceName ??
    null;
  const selectedRepositoryName =
    selectedConfiguration?.repositoryName ??
    environment?.templates?.find(
      (template) => template.id === selectedWorkspaceId,
    )?.machine?.folderName ??
    environment?.configured?.repositoryName ??
    null;
  const selectionOptions = useMemo(() => {
    const options = [
      ...(environment?.configurations ?? []).map((configuration) => ({
        workspaceId: configuration.workspaceId,
        repositoryId: configuration.repositoryId,
        workspaceName: configuration.workspaceName,
        repositoryName: configuration.repositoryName,
        label: `${configuration.workspaceName} · ${configuration.repositoryName}`,
      })),
      ...(environment?.configured &&
      !environment.configurations?.some(
        (configuration) =>
          configuration.workspaceId === environment.configured?.workspaceId &&
          configuration.repositoryId === environment.configured?.repositoryId,
      )
        ? [
            {
              workspaceId: environment.configured.workspaceId,
              repositoryId: environment.configured.repositoryId,
              workspaceName: environment.configured.workspaceName,
              repositoryName: environment.configured.repositoryName ?? "",
              label: [
                environment.configured.workspaceName,
                environment.configured.repositoryName,
              ]
                .filter(Boolean)
                .join(" · "),
            },
          ]
        : []),
      ...(environment?.templates ?? []).map((template) => ({
        workspaceId: template.id,
        repositoryId: null,
        workspaceName: template.name,
        repositoryName:
          template.machine?.folderName ?? template.description ?? "",
        label: [
          template.name,
          template.machine?.folderName ?? template.description,
        ]
          .filter(Boolean)
          .join(" · "),
        machine: template.machine,
      })),
    ];
    return options;
  }, [environment]);
  const workspaceOptions = useMemo(
    () => [
      ...new Map(
        selectionOptions.map((option) => [option.workspaceId, option]),
      ).values(),
    ],
    [selectionOptions],
  );
  const repositoryOptions = useMemo(
    () =>
      selectionOptions.filter(
        (option) =>
          option.workspaceId === selectedWorkspaceId &&
          option.repositoryId !== null,
      ),
    [selectedWorkspaceId, selectionOptions],
  );
  const selectedTemplate = environment?.templates?.find(
    (template) => template.id === selectedWorkspaceId,
  );
  const selectedWorkspaceType =
    selectedTemplate?.type ?? environment?.configured?.type;
  const selectedWorkspaceMode = normalizeAgentWorkspaceMode(
    selectedTemplate?.workspaceMode ?? environment?.configured?.workspaceMode,
  );
  const selectedWorkspaceDescription = selectedTemplate?.description;
  const previewAvailable =
    selectedWorkspaceType === "container" &&
    developmentConfigured &&
    (selectedWorkspaceName?.toLowerCase().includes("flutter") ?? false);
  const selectedOption = selectionOptions.find(
    (option) => option.workspaceId === selectedWorkspaceId,
  );
  const selectedMachine =
    (selectedOption && "machine" in selectedOption
      ? selectedOption.machine
      : undefined) ??
    environment?.configured?.machine ??
    null;
  const selectedIsMachine =
    selectedTemplate?.type === "machine" ||
    environment?.configured?.type === "machine";
  const gitAvailable =
    selectedWorkspaceMode === "machine"
      ? developmentConfigured
      : selectedIsMachine
        ? Boolean(selectedMachine?.git?.available)
        : isContainerWorkspaceType(selectedWorkspaceType ?? "") &&
          Boolean(selectedRepositoryId) &&
          developmentConfigured;
  useEffect(() => {
    if (!gitAvailable && (tab === "git" || tab === "log")) setTab("terminal");
  }, [gitAvailable, tab]);
  useEffect(() => {
    setMountedTabs((current) => {
      if (current.has(tab)) return current;
      return new Set(current).add(tab);
    });
  }, [tab]);
  return (
    <aside
      ref={panel}
      style={
        { "--development-panel-width": `${width}px` } as React.CSSProperties
      }
      className="development-panel fixed inset-y-0 right-0 z-40 flex min-h-0 min-w-0 shrink-0 flex-col overflow-hidden border-l border-stone-200 bg-white shadow-2xl md:relative md:inset-auto md:z-auto md:shadow-none dark:border-white/10 dark:bg-[#151918]"
    >
      <button
        type="button"
        onPointerDown={resize}
        className="absolute inset-y-0 left-0 z-30 hidden w-3 cursor-col-resize touch-none items-center justify-center border-l-2 border-transparent text-stone-300 hover:border-lumen-500 hover:bg-lumen-50/60 hover:text-lumen-600 md:flex dark:text-stone-600 dark:hover:bg-lumen-900/30 dark:hover:text-lumen-300"
        aria-label={t("Resize development panel")}
      >
        <GripVertical size={12} />
      </button>
      <div className="flex h-14 min-w-0 shrink-0 items-center overflow-x-auto border-b border-stone-200 pr-3 pl-4 dark:border-white/10">
        {approvalsAvailable && (
          <button
            className={`flex h-full shrink-0 items-center gap-1.5 border-b-2 px-3 text-xs ${tab === "approvals" ? "border-lumen-500 text-lumen-700" : "border-transparent text-stone-500"}`}
            onClick={() => setTab("approvals")}
          >
            <ShieldCheck size={14} /> {t("Command approvals")}
          </button>
        )}
        <button
          className={`flex h-full shrink-0 items-center gap-1.5 border-b-2 px-3 text-xs ${tab === "usage" ? "border-lumen-500 text-lumen-700" : "border-transparent text-stone-500"}`}
          onClick={() => setTab("usage")}
        >
          <BarChart3 size={14} /> {t("Usage")}
        </button>
        {selectedMention && (
          <button
            type="button"
            onClick={() => setTab("mention")}
            className={`h-full max-w-56 shrink-0 truncate border-b-2 px-3 text-xs ${tab === "mention" ? "border-lumen-500 text-lumen-700" : "border-transparent text-stone-500"}`}
          >
            {selectedMention.reference}
          </button>
        )}
        {conversationArtifacts && conversationArtifacts.length > 0 && (
          <button
            className={`flex h-full shrink-0 items-center gap-1.5 border-b-2 px-3 text-xs ${tab === "artifacts" ? "border-lumen-500 text-lumen-700" : "border-transparent text-stone-500"}`}
            onClick={() => setTab("artifacts")}
          >
            <Package size={14} /> {t("Artifacts")}
          </button>
        )}
        {developmentConfigured && (
          <>
            <button
              className={`flex h-full shrink-0 items-center gap-1.5 border-b-2 px-3 text-xs ${tab === "terminal" ? "border-lumen-500 text-lumen-700" : "border-transparent text-stone-500"}`}
              onClick={() => setTab("terminal")}
            >
              <TerminalSquare size={14} /> {t("Terminal")}
            </button>
            {previewAvailable && (
              <button
                className={`flex h-full shrink-0 items-center gap-1.5 border-b-2 px-3 text-xs ${tab === "preview" ? "border-lumen-500 text-lumen-700" : "border-transparent text-stone-500"}`}
                onClick={() => setTab("preview")}
              >
                <Monitor size={14} /> {t("Preview")}
              </button>
            )}
            {gitAvailable && (
              <button
                className={`flex h-full shrink-0 items-center gap-1.5 border-b-2 px-3 text-xs ${tab === "git" ? "border-lumen-500 text-lumen-700" : "border-transparent text-stone-500"}`}
                onClick={() => setTab("git")}
              >
                <GitBranch size={14} /> Git
              </button>
            )}
            {gitAvailable && (
              <button
                className={`flex h-full shrink-0 items-center gap-1.5 border-b-2 px-3 text-xs ${tab === "log" ? "border-lumen-500 text-lumen-700" : "border-transparent text-stone-500"}`}
                onClick={() => setTab("log")}
              >
                <History size={14} /> {t("Log")}
              </button>
            )}
            {openFiles.map((path) => (
              <button
                key={path}
                title={path}
                className={`group flex h-full max-w-44 min-w-24 shrink-0 items-center gap-1.5 border-b-2 px-3 text-xs ${tab === path ? "border-lumen-500 text-lumen-700" : "border-transparent text-stone-500"}`}
                onClick={() => setTab(path)}
              >
                <FileCode2 size={13} className="shrink-0" />
                <span className="truncate">{path.split("/").pop()}</span>
                <span
                  role="button"
                  aria-label={`Close ${path}`}
                  className="ml-auto rounded p-0.5 hover:bg-stone-200 dark:hover:bg-white/10"
                  onClick={(event) => {
                    event.stopPropagation();
                    closeFile(path);
                  }}
                >
                  <X size={12} />
                </span>
              </button>
            ))}
          </>
        )}
        <button
          className="button shrink-0 p-1.5"
          onClick={onClose}
          aria-label={t("Close side panel")}
        >
          <X size={16} />
        </button>
      </div>
      <div
        className={`${tab === "mention" ? "hidden" : "flex"} min-h-10 shrink-0 items-center gap-2 border-b border-stone-200 px-3 dark:border-white/10`}
      >
        {developmentConfigured && (
          <button
            type="button"
            className="button shrink-0 px-2 py-1.5 text-xs"
            onClick={() => {
              const query = new URLSearchParams();
              if (selectedWorkspaceId)
                query.set("workspace", selectedWorkspaceId);
              if (selectedRepositoryId)
                query.set("repository", selectedRepositoryId);
              if (openFiles.includes(tab)) query.set("file", tab);
              const prefix = location.pathname.startsWith("/projects/")
                ? "/projects"
                : "";
              navigate(`${prefix}/c/${conversationId}/workspace?${query}`);
            }}
          >
            <Maximize2 size={14} /> {t("Open editor")}
          </button>
        )}
        <span
          className="min-w-0 flex-1 truncate text-xs font-medium text-stone-600 dark:text-stone-300"
          title={selectedWorkspaceName ?? undefined}
        >
          {selectedWorkspaceName ?? t("Development workspace")}
          {selectedRepositoryName ? ` · ${selectedRepositoryName}` : ""}
        </span>
        {workspaceOptions.length > 0 && (
          <select
            className="max-w-[28%] min-w-0 rounded-md border border-stone-200 bg-transparent px-1.5 py-1 text-[11px] text-stone-600 outline-none dark:border-white/10 dark:text-stone-300"
            value={selectedWorkspaceId ?? ""}
            aria-label={t("Select development workspace")}
            onChange={(event) => {
              const workspaceId = event.target.value || null;
              const firstRepository = selectionOptions.find(
                (option) => option.workspaceId === workspaceId,
              );
              setSelectedWorkspaceId(workspaceId);
              setSelectedRepositoryId(firstRepository?.repositoryId ?? null);
            }}
          >
            {workspaceOptions.map((option) => (
              <option key={option.workspaceId} value={option.workspaceId}>
                {option.workspaceName}
                {environment?.templates?.find(
                  (template) => template.id === option.workspaceId,
                )?.workspaceMode === "machine"
                  ? ` · ${t("Machine")} · ${t("Shared")}`
                  : ""}
              </option>
            ))}
          </select>
        )}
        {repositoryOptions.length > 0 && (
          <select
            className="max-w-[38%] min-w-0 rounded-md border border-stone-200 bg-transparent px-1.5 py-1 text-[11px] text-stone-600 outline-none dark:border-white/10 dark:text-stone-300"
            value={selectedRepositoryId ?? ""}
            aria-label={t("Select repository")}
            onChange={(event) =>
              setSelectedRepositoryId(event.target.value || null)
            }
          >
            {repositoryOptions.map((option) => (
              <option
                key={`${option.workspaceId}:${option.repositoryId ?? ""}`}
                value={option.repositoryId ?? ""}
              >
                {option.repositoryName}
              </option>
            ))}
          </select>
        )}
      </div>
      {tab !== "mention" && selectedWorkspaceMode === "machine" && (
        <div className="flex shrink-0 items-start gap-2 border-b border-lumen-200 bg-lumen-50 px-3 py-2 text-xs text-lumen-900 dark:border-lumen-800 dark:bg-lumen-950/40 dark:text-lumen-100">
          <Server size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
          <div className="min-w-0">
            <p className="font-medium">{t("Shared machine workspace")}</p>
            <p className="mt-0.5 text-lumen-800 dark:text-lumen-200">
              {t(
                "Your conversations using this workspace share files and Git state. Concurrent edits may affect the same files.",
              )}
            </p>
            {selectedWorkspaceDescription && (
              <p className="mt-1 text-lumen-800 dark:text-lumen-200">
                {selectedWorkspaceDescription}
              </p>
            )}
          </div>
        </div>
      )}
      <div className="relative min-h-0 min-w-0 flex-1 overflow-hidden">
        {tab === "mention" && (
          <div className="absolute inset-0 min-w-0 overflow-y-auto">
            <ChatMentionDetails conversationId={conversationId} />
          </div>
        )}
        {developmentConfigured && (
          <>
            {mountedTabs.has("terminal") && (
              <div
                className={`absolute inset-0 min-w-0 overflow-hidden ${tab === "terminal" ? "" : "hidden"}`}
              >
                <TerminalTab
                  conversationId={conversationId}
                  selection={selection}
                />
              </div>
            )}
            {previewAvailable && mountedTabs.has("preview") && (
              <div
                className={`absolute inset-0 min-w-0 overflow-hidden ${tab === "preview" ? "" : "hidden"}`}
              >
                <PreviewTab
                  conversationId={conversationId}
                  selection={selection}
                />
              </div>
            )}
            {gitAvailable && mountedTabs.has("git") && (
              <div
                className={`absolute inset-0 min-w-0 overflow-hidden ${tab === "git" ? "" : "hidden"}`}
              >
                <GitTab
                  active={tab === "git"}
                  conversationId={conversationId}
                  selection={selection}
                  onOpenFile={openFile}
                />
              </div>
            )}
            {gitAvailable && mountedTabs.has("log") && (
              <div
                className={`absolute inset-0 min-w-0 overflow-hidden ${tab === "log" ? "" : "hidden"}`}
              >
                <GitLogTab
                  active={tab === "log"}
                  conversationId={conversationId}
                  selection={selection}
                />
              </div>
            )}
            {openFiles.map(
              (path) =>
                mountedTabs.has(path) && (
                  <div
                    key={path}
                    className={`absolute inset-0 min-w-0 overflow-hidden ${tab === path ? "" : "hidden"}`}
                  >
                    <FileTab
                      conversationId={conversationId}
                      selection={selection}
                      path={path}
                    />
                  </div>
                ),
            )}
          </>
        )}
        {approvalsAvailable && mountedTabs.has("approvals") && (
          <div
            className={`absolute inset-0 min-w-0 overflow-hidden ${tab === "approvals" ? "" : "hidden"}`}
          >
            <ConversationCommandRulesPanel conversationId={conversationId} />
          </div>
        )}
        {conversationArtifacts && mountedTabs.has("artifacts") && (
          <div
            className={`absolute inset-0 min-w-0 overflow-y-auto ${tab === "artifacts" ? "" : "hidden"}`}
          >
            <ChatArtifactsTab
              artifacts={conversationArtifacts}
              loading={artifactsLoading}
              error={artifactsError}
              reload={reloadArtifacts}
            />
          </div>
        )}
        {mountedTabs.has("usage") && (
          <div
            className={`absolute inset-0 min-w-0 overflow-hidden ${tab === "usage" ? "" : "hidden"}`}
          >
            <ChatUsagePanel
              conversationId={conversationId}
              usageRevision={usageRevision}
            />
          </div>
        )}
      </div>
    </aside>
  );
}
